import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from '../occasions/shared/OccasionPageGrid'

import heroImg from 'assets/img/lease-truck-program-hero.jpg'
import gridImage1 from 'assets/img/lease-truck-program-grid-1.jpeg'
import gridImage2 from 'assets/img/lease-truck-program-grid-2.jpg'
import infographic1 from 'assets/img/lease-truck-program-4.png'
import infographic2 from 'assets/img/lease-truck-program-5.png'
import leaseOption1 from 'assets/img/lease-option-1.jpg'
import leaseOption2 from 'assets/img/lease-option-2.jpg'
import leaseClients from 'assets/img/lease-a-truck-clients.png'

export default function LeaseTruckProgramPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  return (
    <Layout>
      <Helmet>
        <title>Food Truck Lease Program - Roaming Hunger</title>
        <meta name="og:title" content="Food Truck Lease Program"></meta>
        <meta name="description" content="Take the risk out of building a food truck business with Roaming Hunger's Lease Program."></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="The Roaming Hunger Food Truck Lease Program"
          onButtonClick={openModal}
          description={(
            <div>
              Take the risk out of building a food truck business.
            </div>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}
      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          So Much More Than a Food Truck Lease
        </h2>
        <p className="font-light text-gray-900 mt-6">
          Since 2009, Roaming Hunger has partnered with hard-working mobile vendors, entrepreneurs,
          and chefs whose passion inspires and drives us and our communities. Our mission is to take
          the risk out of building a food truck business and help you be successful beyond getting
          your truck on the road. We’ll help you navigate the building process, provide you with marketing,
          and help you find events and catering opportunities. And that’s just the beginning…
        </p>
        <p className="font-light text-gray-900 mt-6">
          Looking to rent a food truck for a marketing promotion? Click here to go to our <a href="/experiential-marketing-agency/" target="_blank" className="text-blue">Agency Page.</a>
        </p>
        <p className="font-light text-gray-900 mt-6">
          For short-term rentals please fill out our contact form on <a href="/start-a-food-truck/" target="_blank" className="text-blue">this page.</a>
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Lease Program Benefits"
          content={
            <div className="my-10">
            <p className="font-light text-gray-900 mt-6">
              Get a three year lease-to-own program that sets you up for success. Total Roaming Hunger program benefits are valued at up to $90,000 over the life of the program and include:
            </p>
            <ul className='list-outside pl-5 list-disc my-6'>
              <li>Professional food truck wrap design ($1,500)</li>
              <li>High-quality, professional website ($2,500)</li>
              <li>Social media marketing and PR support to kickstart your business ($2,000)</li>
              <li>Minimum of one catering opportunity from Roaming Hunger per month (estimated $1,000+ per booked event)</li>
              <li>Free access to Roaming Hunger public event listings to fill your calendar(estimated $800 + per booked event)</li>
              <li>Free food truck marketing profile on Roaming Hunger website</li>
              <li>If you choose to sell your truck at end of term, Roaming Hunger will help sell your truck and waive any listing fees</li>
            </ul>
            </div>
          }
          image={gridImage1}
        />
        <OccasionPageGridRow
          heading="“Roaming Hunger is an excellent company and resource for anyone that wants to start a food truck. Their team of people are highly professional and supportive.”"
          content={
            <p className="my-2">Laura Christie, Christie Caters Farm To Truck</p>
          }
          image={gridImage2}
          reverse
        />
        <br />
        <OccasionPageGridRow
          heading="Layout & Equipment: Option 1"
          content={
            <>
            <p className="font-light text-gray-900 mt-2">A specialty truck equipped for menus designed around a grill and/or a deep fryer like wings, burgers, or loaded fries. Includes:</p>
            <ul className='list-outside pl-5 list-disc my-4'>
              <li>New Ford Gasoline (F59) or Freightliner Diesel (MT45/MT55) chassis - depending on availability:</li>
              <li>A/C: 2 units - 15,000 BTU</li>
              <li>ANSUL fire suppression system</li>
              <li>Generator: 12KW commercial diesel</li>
              <li>Commercial hood and fan</li>
              <li>True T - 19 refrigerator – single door</li>
              <li>True sandwich prep unit</li>
              <li>True T - 19F freezer - single door</li>
              <li>Flat top griddle - 36”</li>
              <li>2 - Basket deep fryer - 2 units</li>
              <li>Charbroiler grill - 36”</li>
              <li>Work tables - will fill in based on leftover space</li>
              <li>Standard 3 - bay and hand wash(w / drain boards)</li>
              <li>Serving window, including screen</li>
              <li>Stereo receiver and external speakers</li>
              <li>Internal and external lighting</li>
            </ul>
            </>
          }
          image={leaseOption1}
        />
        <br />
        <OccasionPageGridRow
          heading="Layout & Equipment: Option 2"
          content={
            <>
              <p className="font-light text-gray-900 mt-2">A great all-purpose kitchen layout ideal for a huge variety of cuisines including tacos, sandwiches, bowls and more. Includes:</p>
              <ul className='list-outside pl-5 list-disc my-4'>
                <li>New Ford Gasoline (F59) or Freightliner Diesel (MT45/MT55) chassis - depending on availability</li>
                <li>A/C: 2 units - 15,000 BTU</li>
                <li>ANSUL fire suppression system</li>
                <li>Generator: 12KW commercial diesel</li>
                <li>Commercial hood and fan</li>
                <li>True T-19 refrigerator – single door</li>
                <li>True sandwich prep unit</li>
                <li>True T-19F freezer - single door</li>
                <li>Flat top griddle</li>
                <li>2-Basket deep fryer - 1 unit</li>
                <li>4-Burner oven and range combo</li>
                <li>Work tables - will fill in based on leftover space</li>
                <li>Standard 3 - bay and hand wash(w / drain boards)</li>
                <li>Serving window, including screen</li>
                <li>Stereo receiver and external speakers</li>
                <li>Internal and external lighting</li>
              </ul>
            </>
          }
          image={leaseOption2}
        />
      </OccasionPageGrid>
      <div className="container">
        <img src={infographic1} className="my-8" />
        <img src={infographic2} className="my-8" />
      </div>


      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="A Few Companies We've Worked With"
          content="We've talked to thousands of people over the years, from big brands to solo entrepreneurs just starting out. Each unique concept requires a one-on-one conversation and we try to steer people in the right direction for them, whether it's getting a truck with us or with someone else."
          image={leaseClients}
          reverse
        />

      </OccasionPageGrid>
    </Layout>
  )
}
